"use client"
import { cn } from "@/utils/cn"
import { useCallback, useEffect, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"
import { EmblaCarouselType } from "embla-carousel"
import { SlideOnView } from "@/components/slide-on-view"
import { RichText, RichTextValue } from "@/components/rich-text"
import { Cta, SanityCtaProps } from "@/components/cta"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { MotionValue, useMotionValue, useTransform } from "motion/react"
import { motion } from "motion/react"
import { PlayIcon } from "@/svgs/play-icon"
import { useBreakpoint } from "@/ui/hooks/useBreakpoint"
import Image from "next/image"

interface VideosProps {
  content: {
    variant: "white" | "blue"
    title: RichTextValue
    description: string
    cta: SanityCtaProps
  }
}

const slides = [1, 2, 3, 4, 5]

export function Videos({ content }: VideosProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
  })
  const scrollValue = useMotionValue(0)
  const [currentSlide, setCurrentSlide] = useState(0)
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCurrentSlide(emblaApi.selectedScrollSnap())
  }, [])

  const { isScreenLessThan } = useBreakpoint()

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)

    emblaApi.on("select", onSelect)
    emblaApi.on("scroll", (api) => {
      const rounded = Math.abs(Math.round(api.scrollProgress() * 1000) / 1000)
      scrollValue.set(rounded)
    })
  }, [emblaApi, onSelect])

  return (
    <div
      className={cn(
        /**
         * TODO: hidden for now
         */
        "relative hidden overflow-hidden px-4 py-16",
        content.variant === "white"
          ? "bg-neutral-light text-neutral-dark"
          : "bg-primary-800 text-neutral-light"
      )}
    >
      <div
        className={cn(
          "relative z-10 max-w-[63rem] lg:max-w-[90rem]",
          content.variant === "white" ? "mx-auto" : "md:mx-16 lg:mx-20 xl:mx-35"
        )}
      >
        <SlideOnView>
          <RichText value={content.title} />
        </SlideOnView>
        <SlideOnView delay={0.3} className="mt-6 text-lg xl:mt-8 xl:text-xlg">
          {content.description}
        </SlideOnView>
        {content.cta?.label && (
          <SlideOnView delay={0.5}>
            <Cta className="mt-8" sanity={content.cta} />
          </SlideOnView>
        )}
      </div>
      <div className="relative mx-auto mt-10 w-full max-w-full md:max-w-300">
        <div ref={emblaRef}>
          <div className="flex touch-pan-y touch-pinch-zoom">
            {slides.map((_, index) => (
              <Slide
                key={index}
                scrollValue={scrollValue}
                isActive={index === currentSlide}
                scrollCenter={index * (1 / slides.length)}
                scrollStep={1 / slides.length}
                shadowClassName={
                  index < currentSlide
                    ? "bg-gradient-to-l"
                    : index > currentSlide
                      ? "bg-gradient-to-r"
                      : ""
                }
                pullFactor={isScreenLessThan("lg") ? 0 : 2000}
              >
                <Image
                  fill
                  alt="1"
                  src={`/placeholders/${(index % 3) + 1}.png`}
                />
              </Slide>
            ))}
          </div>
        </div>
        <div className="mt-8 flex items-center justify-center gap-4">
          <ArrowLeft
            size={40}
            className="p-2 text-grey-150"
            strokeWidth={3}
            onClick={() => emblaApi?.scrollPrev()}
          />
          {slides.map((_, index) => {
            const isActive = index === currentSlide
            return (
              <button
                key={index}
                className={cn(
                  "rounded-full transition-all duration-300",
                  isActive ? "h-4 w-4 bg-viola-500" : "h-3 w-3 bg-grey-100"
                )}
                onClick={() => emblaApi?.scrollTo(index)}
              />
            )
          })}
          <ArrowRight
            size={40}
            strokeWidth={3}
            className="p-2 text-grey-150"
            onClick={() => emblaApi?.scrollNext()}
          />
        </div>
      </div>
    </div>
  )
}

function Slide({
  children,
  scrollValue,
  isActive,
  scrollStep,
  scrollCenter,
  shadowClassName,
  pullFactor,
}: {
  children: React.ReactNode
  scrollValue: MotionValue<number>
  isActive: boolean
  scrollCenter: number
  scrollStep: number
  shadowClassName: string
  pullFactor: number
}) {
  const distanceFromCenter = useTransform(scrollValue, (scroll) => {
    const distance = scroll - scrollCenter
    if (distance > 0.5) {
      return distance - 1
    }
    if (distance < -0.5) {
      return distance + 1
    }
    return distance
  })
  const scale = useTransform(
    distanceFromCenter,
    [-scrollStep * 2, 0, scrollStep * 2],
    [0.6, 1, 0.6]
  )
  const translateX = useTransform(
    distanceFromCenter,
    (distance) => distance * pullFactor
  )
  const zIndex = useTransform(
    distanceFromCenter,
    [-scrollStep * 2, 0, scrollStep * 2],
    [0, 100, 0]
  )
  const opacity = useTransform(
    distanceFromCenter,
    [-scrollStep * 3, -scrollStep, scrollStep, scrollStep * 3],
    [0, 1, 1, 0]
  )

  return (
    <motion.div
      className="relative flex min-w-0 flex-[0_0_100%] translate-3d items-center justify-center lg:flex-[0_0_50%]"
      style={{
        zIndex,
        opacity,
      }}
    >
      <motion.div className="relative" style={{ scale, translateX }}>
        <div
          className={cn(
            "relative h-50 w-90 overflow-hidden rounded-[4rem] transition-opacity duration-300 md:h-120 md:w-200"
          )}
        >
          {children}
          <PlayIcon className="absolute top-1/2 left-1/2 -translate-1/2" />
          <div
            className={cn(
              "from-black/60 to-black/0 pointer-events-none absolute inset-0 h-full w-full transition-opacity duration-300",
              isActive ? "opacity-0" : "opacity-20",
              shadowClassName
            )}
          />
        </div>
      </motion.div>
    </motion.div>
  )
}
